import * as React from 'react'
import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import './StyleSheet.css'
import sendIcon from '../assets/send_icon.png'
import googleIcon from '../assets/google-icon.png'
import FileSelectionAccordion from './FileSelectionAccordion'

/**
 * Footer Component
 *
 * The main input area for the chat interface. It includes:
 * 1. The message textarea (auto-resizing).
 * 2. The Send / Stop Generating buttons.
 * 3. A "Grounding" toggle (specific to LLM source).
 * 4. The `FileSelectionAccordion` (conditionally rendered when uploading files).
 *
 * It handles validation logic to ensure a message cannot be sent without
 * valid input or active data sources, and manages UI states for errors and warnings.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onSendMessage - Callback to trigger sending a message to the backend.
 * @param {string} props.error - Error message to display above the chat input.
 * @param {boolean} props.isLoading - Global loading state, disables inputs when true.
 * @param {string} props.activeButton - The currently selected data source (e.g., 'LLM', 'file_upload', 'DocLib').
 * @param {Array} props.files - Array of uploaded files available for selection.
 * @param {Function} props.onToggleFileSelection - Callback to toggle the selection state of a specific file.
 * @param {Function} props.onSelectAll - Callback to toggle the selection state of all files.
 * @param {boolean} props.isUploading - Indicates if a file is currently being uploaded.
 * @param {boolean} props.showRevaluationWarning - Flag to show a warning prompting the user to re-evaluate selected files.
 * @param {Function} props.onDismissRevaluationWarning - Callback to dismiss the revaluation warning.
 * @param {Object} props.docLibFilters - Currently applied filters for the DocLib data source.
 * @param {Function} props.onRemoveDocLibFilter - Callback to remove a specific DocLib filter.
 * @param {boolean} props.isGroundingEnabled - State indicating whether Google Grounding is toggled on.
 * @param {Function} props.onGroundingChange - Callback to toggle the Grounding state.
 * @param {boolean} props.isGenerating - Indicates if the LLM is currently streaming/generating a response.
 * @param {Function} props.onStopGenerating - Callback to abort the ongoing LLM generation.
 * @param {boolean} props.isStopping - Indicates if the stop request is currently being processed.
 */
const Footer = ({
  onSendMessage,
  error,
  isLoading,
  activeButton,
  files,
  onToggleFileSelection,
  onSelectAll,
  isUploading,
  showRevaluationWarning,
  onDismissRevaluationWarning,
  docLibFilters,
  onRemoveDocLibFilter,
  isGroundingEnabled,
  onGroundingChange,
  isGenerating,
  onStopGenerating,
  isStopping,
  isEditMode,
  onEditModeChange,  
}) => {
  const [question, setQuestion] = useState('')
  const textareaRef = useRef(null)

  // Determines if the file selection error should be shown.
  const isAnyFileSelected = files.some(file => file.isSelected)
  const showFileSelectionError =
    activeButton === 'file_upload' && files.length > 0 && !isAnyFileSelected

  // Check if DocLib is active AND if both filter arrays are empty
  const isDocLibActive = activeButton === 'DocLib'
  const hasDocLibFilters =
    docLibFilters &&
    (docLibFilters.docTypes &&
      docLibFilters.docTypes.length > 0 &&
      (docLibFilters.customerAreas && docLibFilters.customerAreas.length > 0))

  const showDocLibError = isDocLibActive && !hasDocLibFilters

  let placeholderText = 'Enter your question'
  if (!activeButton) {
    placeholderText = 'Please select a source'
  } else if (showRevaluationWarning) {
    placeholderText = 'Please review new files'
  } else if (showFileSelectionError) {
    placeholderText = 'Select a file to enable chat'
  } else if (showDocLibError) {
    placeholderText = 'Please apply a filter'
  }

  /**
     * Effect to automatically reset the grounding toggle.
     * If the user switches away from the 'LLM' data source, this ensures the
     * grounding button is turned off for other sources where it's not applicable.
     */
  useEffect(
    () => {
      if (activeButton !== 'LLM') {
        onGroundingChange(false)
      }
    },
    [activeButton]
  )

  /**
    * Effect to auto-resize the textarea height as the user types.
    * It resets the height to 'auto' to allow shrinking, then sets it to the
    * `scrollHeight` to fit the content, up to the CSS max-height.
    */
  useEffect(
    () => {
      const textarea = textareaRef.current
      if (textarea) {
        if (question === '') {
          textarea.style.height = 'auto'
        } else {
          textarea.style.height = 'auto'
          textarea.style.height = `${textarea.scrollHeight}px`
        }
      }
    },
    [question, activeButton, files.length, isAnyFileSelected]
  )

  // This logic correctly determines if the button should be disabled.
  const isSendDisabled =
    isLoading ||
    !activeButton ||
    showFileSelectionError ||
    isUploading ||
    showRevaluationWarning ||
    showDocLibError ||
    isStopping

  /**
     * Handles sending the message. It performs a guard check to ensure a message
     * isn't sent when it's disabled or empty, then calls the parent function
     * and clears the input.
     */
  const handleSend = () => {
    if (isSendDisabled || !question.trim()) {
      return
    }
    onSendMessage(question, isGroundingEnabled)
    setQuestion('')
  }
  /**
    * Handles the 'Enter' key press to send the message, while allowing
    * 'Shift+Enter' for new lines.
    */
  const handleKeyPress = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  return (
    <div className='container-fluid'>
      {error &&
        <div className='footer-error'>
          {error}
        </div>}

      {showFileSelectionError &&
        <div className='footer-helper-text footer-helper-error'>
          Please select at least one file to proceed.
        </div>}
      {/* Revaluation Warning with Cross Button */}
      {activeButton === 'file_upload' &&
        showRevaluationWarning &&
        <div className='footer-helper-text footer-helper-warning d-flex justify-content-between align-items-center'>
          <span>Please re-evaluate your file selection.</span>
          <button
            type='button'
            className='btn btn-sm text-black p-0 ml-2'
            onClick={onDismissRevaluationWarning}
            style={{
              textDecoration: 'none',
              fontSize: '1.2rem',
              lineHeight: '1'
            }}
            aria-label='Close warning'
          >
            &times;
          </button>
        </div>}
      {/* DocLib Mandatory Filter Warning */}
      {showDocLibError &&
        <div className='footer-helper-text footer-helper-error'>
          Please apply filter to query doclib data source.
        </div>}

      {!activeButton &&
        <div className='footer-helper-text'>
          Please select a data source to initiate the chat.
        </div>}

      {/* Conditionally renders the file selection accordion. */}
      {activeButton === 'file_upload' &&
        files &&
        files.length > 0 &&
        <FileSelectionAccordion
          files={files}
          onToggleFileSelection={onToggleFileSelection}
          onSelectAll={onSelectAll}
          isLoading={isLoading}
          isUploading={isUploading}
        />}
      <div className='row'>
        <div className='col-12 pr-0 d-flex align-items-end'>
           {/* Custom Toggle Switch (Matches your image) */}
          <div
            onClick={() => {
              if (!isLoading && !isUploading) {
                onEditModeChange(!isEditMode);
              }
            }}
            title="Toggle Edit Mode"
            style={{
              width: '40px',
              height: '21px',
              borderRadius: '15px',
              border: `1px solid ${isEditMode ? '#022366' : '#888'}`, // Matches your theme when ON
              backgroundColor: isEditMode ? '#022366' : '#f9f9f9',
              position: 'relative',
              cursor: (isLoading || isUploading) ? 'not-allowed' : 'pointer',
              opacity: (isLoading || isUploading) ? 0.5 : 1,
              transition: 'all 0.3s ease',
              marginRight: '3px',
              marginLeft: '-11px',    
              marginBottom: '11px', // Aligns vertically with the input text
              flexShrink: 0
            }}
          >
            {/* The Circle (Thumb) */}
            <div style={{
              width: '14px',
              height: '14px',
              borderRadius: '48%',
              backgroundColor: isEditMode ? '#fff' : '#666',
              position: 'absolute',
              top: '2.5px',
              left: isEditMode ? '22px' : '3px', // Slides right when ON
              transition: 'all 0.3s ease'
            }} />
            
            {/* The Text ("On" / "Off") */}
            <span style={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              left: isEditMode ? '7px' : '21px', // Adjusts position based on state
              fontSize: '9px',
              fontWeight: '500',
              color: isEditMode ? '#fff' : '#555',
              transition: 'all 0.3s ease',
              userSelect: 'none',
              fontFamily: 'Segoe UI, sans-serif'
            }}>
              {isEditMode ? 'On' : 'Off'}
            </span>
          </div>  
          {/* <button
            type='button'
            onClick={() => onEditModeChange(!isEditMode)}
            title={isEditMode ? 'Edit Mode is ON' : 'Edit Mode is OFF'}
            disabled={isLoading || isUploading}
            style={{
              // Styling to make it look like a nice circular toggle button
              background: isEditMode ? '#022366' : 'transparent', // Fills blue when ON
              border: '1px solid #022366',
              borderRadius: '50%',
              width: '38px',
              height: '38px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '10px', // Adds space between this button and the text box
              marginBottom: '4px', // Aligns it perfectly with the bottom of the text box
              cursor: 'pointer',
              flexShrink: 0, // Prevents the button from being squished
              transition: 'all 0.2s ease-in-out',
              opacity: (isLoading || isUploading) ? 0.5 : 1
            }}
          >
            <svg 
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke={isEditMode ? "white" : "#022366"} // White when ON, Blue when OFF
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M12 20h9"></path>
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
            </svg>
          </button>  */} 
          <div id='grow-wrap' className='input-wrapper'>
            <textarea
              ref={textareaRef}
              id='question'
              className='form-control text_area_wrapper'
              placeholder={placeholderText}
              rows='1'
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSendDisabled}
            />

            {/* Grounding Button */}
            {/* Only show this button when LLM is the active source */}
            {activeButton === 'LLM' &&
              <button
                type='button'
                className={`grounding-btn ${isGroundingEnabled
                  ? 'active'
                  : ''}`}
                onClick={() => onGroundingChange(!isGroundingEnabled)}
                title={
                  isGroundingEnabled ? 'Grounding is ON' : 'Grounding is OFF'
                }
                disabled={isLoading || isUploading}
              >
                <img src={googleIcon} alt='Google Grounding' />
              </button>}
          </div>
          {/* Render send button */}
          <button
            type='button'
            className='btn ask-btn'
            onClick={handleSend}
            disabled={isSendDisabled}
          >
            <img src={sendIcon} alt='send_icon' />
          </button>
        </div>
      </div>
    </div>
  )
}

Footer.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  error: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  activeButton: PropTypes.string,
  files: PropTypes.array.isRequired,
  onToggleFileSelection: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  isUploading: PropTypes.bool.isRequired,
  docLibFilters: PropTypes.object
}

Footer.defaultProps = {
  files: [],
  isUploading: false
}

export default Footer
